import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import {
    Card, CardBody, CardHeader, Button, Badge,
    UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from 'reactstrap';
import {
    listPaymentMethods,
    setDefaultPaymentMethod,
    removePaymentMethod,
    ensureCustomer,
    billingActionReset,
} from 'redux/Billing/BillingActions';
import { getUserData } from 'redux/UserAuthentication/LoginActions';
import AddCardModal from './AddCardModal';

const BRAND_ICONS = {
    visa: 'fab fa-cc-visa',
    mastercard: 'fab fa-cc-mastercard',
    amex: 'fab fa-cc-amex',
    discover: 'fab fa-cc-discover',
    diners: 'fab fa-cc-diners-club',
    jcb: 'fab fa-cc-jcb',
};

const PaymentMethodsCard = ({ paymentMethods, loading, hasCustomer, loaded }) => {
    const dispatch = useDispatch();
    const [addOpen, setAddOpen] = useState(false);
    const [actionBusy, setActionBusy] = useState(null); // pmId currently being acted on

    useEffect(() => {
        if (!loaded) {
            dispatch(listPaymentMethods()).catch(() => { /* handled */ });
        }
    }, [dispatch, loaded]);

    const openAdd = async () => {
        if (!hasCustomer) {
            try {
                await dispatch(ensureCustomer());
                dispatch(billingActionReset());
            } catch (err) {
                Swal.fire('Error', err.response?.data?.error || err.message || 'Failed to set up billing.', 'error');
                return;
            }
        }
        setAddOpen(true);
    };

    const handleSetDefault = async (pmId) => {
        setActionBusy(pmId);
        try {
            await dispatch(setDefaultPaymentMethod(pmId));
            await Promise.all([
                dispatch(listPaymentMethods()),
                dispatch(getUserData({ onLogin: true })),
            ]);
            dispatch(billingActionReset());
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message || 'Failed to set default.', 'error');
        } finally {
            setActionBusy(null);
        }
    };

    const handleRemove = async (pmId) => {
        const res = await Swal.fire({
            title: 'Remove this card?',
            text: 'This card will no longer be available for auto-charge or quick-buy.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#f5365c',
        });
        if (!res.isConfirmed) return;

        setActionBusy(pmId);
        try {
            const out = await dispatch(removePaymentMethod(pmId));
            await Promise.all([
                dispatch(listPaymentMethods()),
                dispatch(getUserData({ onLogin: true })),
            ]);
            dispatch(billingActionReset());
            if (out?.autoChargeCancelled) {
                Swal.fire({
                    title: 'Card Removed',
                    text: 'Since this was your only card, your trial auto-charge has been cancelled.',
                    icon: 'info',
                });
            }
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message || 'Failed to remove card.', 'error');
        } finally {
            setActionBusy(null);
        }
    };

    return (
        <>
            <Card className="shadow">
                <CardHeader className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">Saved Cards</h3>
                    <Button color="primary" size="sm" onClick={openAdd}>
                        <i className="fas fa-plus mr-1" /> Add New Card
                    </Button>
                </CardHeader>
                <CardBody>
                    {loading && !loaded && (
                        <div className="text-center text-muted py-3">
                            <i className="fas fa-spinner fa-spin mr-2" /> Loading...
                        </div>
                    )}

                    {loaded && paymentMethods.length === 0 && (
                        <div className="text-center text-muted py-4">
                            <i className="far fa-credit-card fa-2x mb-2 d-block" />
                            {hasCustomer
                                ? <p className="mb-0">No cards on file. Click <strong>Add New Card</strong> to save one.</p>
                                : <p className="mb-0">Add a card to enable quick-buy and manage payments.</p>}
                        </div>
                    )}

                    {paymentMethods.length > 0 && (
                        <div>
                            {paymentMethods.map((pm, idx) => {
                                const iconClass = BRAND_ICONS[pm.brand] || 'far fa-credit-card';
                                const busy = actionBusy === pm.id;
                                const isLast = idx === paymentMethods.length - 1;
                                return (
                                    <div
                                        key={pm.id}
                                        className={`d-flex align-items-center py-3 ${!isLast ? 'border-bottom' : ''}`}
                                    >
                                        <i
                                            className={`${iconClass} mr-3`}
                                            style={{ fontSize: '2.25rem', width: '2.75rem', textAlign: 'center' }}
                                        />
                                        <div className="flex-grow-1" style={{ lineHeight: 1.3 }}>
                                            <div>
                                                <span className="font-weight-bold text-uppercase mr-1">{pm.brand}</span>
                                                <span className="text-muted">•••• {pm.last4}</span>
                                                {pm.isDefault && (
                                                    <Badge color="success" pill className="ml-2">Default</Badge>
                                                )}
                                            </div>
                                            <small className="text-muted">
                                                Expires {String(pm.expMonth).padStart(2, '0')}/{pm.expYear}
                                            </small>
                                        </div>
                                        <UncontrolledDropdown>
                                            <DropdownToggle color="link" className="text-primary p-2" disabled={busy}>
                                                {busy
                                                    ? <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.3rem' }} />
                                                    : <i className="fas fa-ellipsis-v" style={{ fontSize: '1.3rem' }} />}
                                            </DropdownToggle>
                                            <DropdownMenu right>
                                                {!pm.isDefault && (
                                                    <DropdownItem onClick={() => handleSetDefault(pm.id)}>
                                                        <i className="fas fa-star mr-2" /> Set as Default
                                                    </DropdownItem>
                                                )}
                                                <DropdownItem className="text-danger" onClick={() => handleRemove(pm.id)}>
                                                    <i className="fas fa-trash mr-2" /> Remove
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </UncontrolledDropdown>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardBody>
            </Card>

            <AddCardModal
                isOpen={addOpen}
                toggle={() => setAddOpen(v => !v)}
                onAdded={() => dispatch(listPaymentMethods())}
            />
        </>
    );
};

const mapStateToProps = state => ({
    paymentMethods: state.paymentMethods.paymentMethods,
    loading: state.paymentMethods.loading,
    hasCustomer: state.paymentMethods.hasCustomer,
    loaded: state.paymentMethods.loaded,
});

export default connect(mapStateToProps)(PaymentMethodsCard);
